/**
 * FedWatch — fed funds vadelilerinden FOMC olasılıkları (backend: fedwatch.py, macro.db).
 * Tipler, TSİ biçimlendiricileri ve anlık görüntü kancası. Chip ve Makro sekmesi paylaşır.
 */
import { useEffect, useState } from 'react';
import { useWS } from '../WSContext';

export interface FedMeeting {
  meeting: string;          // toplantının son günü, YYYY-MM-DD
  sep: boolean;             // Özet Ekonomik Projeksiyon var mı
  days_to: number;
  start_rate: number;
  end_rate: number;
  exp_change_bp: number;
  p_hike: number; p_hike50: number; p_hold: number; p_cut: number; p_cut50: number;
  cum_moves: number;        // r0'dan bu toplantı sonuna kadar kaç 25bp adım
  method: 'anchor' | 'solve';
  contract: string;
  precision_pp: number;     // yarım baz puanlık tikin olasılık karşılığı
  sofr_check: number | null;
  d1: number | null;        // önceki uzlaşmaya göre değişim (puan)
  d7: number | null;
  thin: boolean;            // kontrat hacmi düşük → fiyat bayat olabilir
}

export interface FedInput { symbol: string; price: number; implied: number; volume: number | null; thin: boolean; }
/** Olayın bir göstergesi — TradingView ekonomik takviminden (konsensüs / önceki / gerçekleşen). */
export interface FedEventItem {
  label: string;
  unit: '%' | 'K' | 'M';
  period: string | null;
  forecast: number | null;  // konsensüs beklentisi
  previous: number | null;  // önceki (revize edilmiş olabilir)
  actual: number | null;
  fc_prev: number | null;   // bir hafta önceki beklenti, değiştiyse
  surprise: number | null;  // gerçekleşen − beklenti
  hawk: -1 | 0 | 1;         // +1 sıkılaştırma yönünde sürpriz
}

export interface FedEvent { ts: number; name: string; items?: FedEventItem[]; }

export interface FedSnapshot {
  ready: boolean;
  error?: string;
  ts?: number;
  kind?: 'intraday' | 'settle';
  trade_date?: string;
  stale?: boolean;
  r0?: number;
  effr_date?: string | null;
  sofr?: number | null;
  target?: [number, number] | null;
  r0_stale?: boolean;
  meetings?: FedMeeting[];
  inputs?: FedInput[];
  events?: FedEvent[];
  cal_ts?: number | null;   // konsensüs takviminin son güncellenmesi
}

export interface FedHistory {
  meeting: string | null;
  settle: { t: number; d: string; p: number; bp: number }[];
  intraday: { t: number; p: number }[];
  events: { t: number; name: string }[];
  error?: string;
}

const TZ = 'Europe/Istanbul';

/** '2026-10-28' → '28 Eki 2026' (takvim günü; saat dilimi kaydırması olmasın diye UTC öğlen) */
export function fmtMeeting(d: string, withYear = true): string {
  return new Date(`${d}T12:00:00Z`).toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'short', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC',
  });
}

/** unix sn → TSİ '30 Eyl 15:30' */
export function fmtTsi(ts: number, withTime = true): string {
  return new Date(ts * 1000).toLocaleString('tr-TR', {
    day: 'numeric', month: 'short', ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}), timeZone: TZ,
  });
}

/** Gösterge değeri: 0.3 → '0.3%', 3 → '3.0%', 90 → '90K', 7.23 → '7.23M' */
export function fmtVal(v: number | null, unit: string): string {
  if (v === null || v === undefined) return '—';
  const n = String(Number(v.toFixed(3)));
  return `${unit === '%' && !n.includes('.') ? `${n}.0` : n}${unit}`;
}

export const pct = (x: number, digits = 0) => `%${(x * 100).toFixed(digits)}`;

/** Net olasılık: artırım pozitif, indirim negatif (yüzde). */
export const netP = (m: FedMeeting) => (m.p_hike - m.p_cut) * 100;

/** Son anlık görüntü. Bağlanınca ve 5 dakikada bir ister; backend her ölçümde de yayınlar. */
export function useFedSnapshot(): FedSnapshot | null {
  const { sendWs, subscribe, connected } = useWS();
  const [snap, setSnap] = useState<FedSnapshot | null>(null);

  useEffect(() => {
    const unsub = subscribe((msg: any) => {
      if (msg?.type === 'fed_path' && msg.payload) setSnap(msg.payload as FedSnapshot);
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    if (!connected) return;
    sendWs({ type: 'get_fed_path' });
    const id = setInterval(() => sendWs({ type: 'get_fed_path' }), 5 * 60_000);
    return () => clearInterval(id);
  }, [connected, sendWs]);

  return snap;
}
